import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppSettings, ListProduct, Section, SectionType } from 'src/app/interfaces';
import { LandingService } from 'src/app/services';
import { GetAppSettings, GetLandinSections, UpdateFavoritesNewIn, UpdateFavoritesProducts } from './landing.actions';

export interface LandingStateModel {
    sections: Section[];
    settings: Partial<AppSettings> | null;
}

@State<LandingStateModel>({
    name: 'landing',
    defaults: {
        sections: [],
        settings: null
    }
})
@Injectable()
export class LandingState {
    constructor(private service: LandingService) {}

    @Selector()
    static sections(state: LandingStateModel) {
        return state.sections;
    }

    @Selector()
    static sellNowUrl(state: LandingStateModel) {
        return state.settings.sellNowUrl;
    }
    @Selector()
    static boutiqueImageUrl(state: LandingStateModel) {
        return state.settings.boutiqueImageUrl;
    }
    @Selector()
    static contactPhoneNumber(state: LandingStateModel) {
        return state.settings.otherSettings.contactPhoneNumber;
    }
    @Selector()
    static accountImage(state: LandingStateModel) {
        return state.settings.specialUrls.accountImage;
    }
    @Selector()
    static favoritesImage(state: LandingStateModel) {
        return state.settings.specialUrls.favoritesImage;
    }

    @Action(GetLandinSections)
    getSections(ctx: StateContext<LandingStateModel>) {
        return this.service.getAll().pipe(
            catchError(() => of({ sections: [] })),
            tap((result) => {
                ctx.patchState({
                    sections: result.sections
                });
            })
        );
    }

    @Action(GetAppSettings)
    getSettings(ctx: StateContext<LandingStateModel>) {
        return this.service.getAppSettings().pipe(
            catchError(() => of(null)),
            tap((result) => {
                ctx.patchState({
                    settings: result
                });
            })
        );
    }

    @Action(UpdateFavoritesNewIn)
    updateFavsNewIn(ctx: StateContext<LandingStateModel>, { payload }: UpdateFavoritesNewIn) {
        ctx.setState(
            patch({
                sections: updateItem<Section>(
                    (section) => section?.type === SectionType.NewIn,
                    patch({
                        products: updateItem<ListProduct>(
                            (product) => product.id === payload.id,
                            patch<ListProduct>({ isFavorite: payload.isFavorite, likes: payload.likes })
                        )
                    })
                )
            })
        );
    }

    @Action(UpdateFavoritesProducts)
    updateFavsProducts(ctx: StateContext<LandingStateModel>, { payload }: UpdateFavoritesProducts) {
        ctx.setState(
            patch({
                sections: updateItem<Section>(
                    (section) => section?.type === SectionType.Products,
                    patch({
                        products: updateItem<ListProduct>(
                            (product) => product.id === payload.id,
                            patch<ListProduct>({
                                isFavorite: payload.isFavorite,
                                likes: payload.likes
                            })
                        )
                    })
                )
            })
        );
    }
}
